import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { CalendarIcon } from "@radix-ui/react-icons";
import { addDays, format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { HTMLAttributes, useState } from "react";
import { DateRange } from "react-day-picker";

export function DatePickerWithRange({
  className,
  defaultDate,
  onChange,
}: {
  defaultDate: DateRange | null | undefined;
  onChange: (date: DateRange | null) => void;
} & Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>) {
  const [date, setDate] = useState<DateRange | null | undefined>(defaultDate);
  const { t, currentLanguage } = useTranslation();

  const handleDateChange = (date?: DateRange) => {
    setDate(date);

    if (date?.from && date?.to && date.from <= date.to) {
      date.from = new Date(date.from.setHours(0, 0, 0, 0));
      date.to = new Date(date.to.setHours(23, 59, 59, 999));
      onChange(date);
    }
  };

  const handleLastRangeChange = (value: string) => {
    const now = new Date();
    let range: DateRange;

    switch (value) {
      case "today":
        range = {
          from: new Date(now.setHours(0, 0, 0, 0)),
          to: new Date(now.setHours(23, 59, 59, 999)),
        };
        break;
      case "yesterday":
        range = {
          from: new Date(addDays(now, -1).setHours(0, 0, 0, 0)),
          to: new Date(addDays(now, -1).setHours(23, 59, 59, 999)),
        };
        break;
      case "thisweek":
        range = {
          from: new Date(now.setDate(now.getDate() - now.getDay() + 1)),
          to: new Date(now.setDate(now.getDate() - now.getDay() + 7)),
        };
        break;
      case "previousweek":
        range = {
          from: new Date(addDays(now, -now.getDay() - 6).setHours(0, 0, 0, 0)),
          to: new Date(addDays(now, -now.getDay()).setHours(23, 59, 59, 999)),
        };
        break;
      case "last7days":
        range = {
          from: new Date(addDays(now, -7).setHours(0, 0, 0, 0)),
          to: new Date(now.setHours(23, 59, 59, 999)),
        };
        break;
      case "thismonth":
        range = {
          from: new Date(now.getFullYear(), now.getMonth(), 1),
          to: new Date(now.getFullYear(), now.getMonth() + 1, 0),
        };
        break;
      case "previousmonth":
        range = {
          from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          to: new Date(now.getFullYear(), now.getMonth(), 0),
        };
        break;
      default:
        return;
    }

    handleDateChange(range);
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-[300px] justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(
                    date.from,
                    currentLanguage === "fr" ? "dd MMM y" : "MMM dd, yyyy",
                    {
                      locale: currentLanguage === "fr" ? fr : enUS,
                    }
                  )}{" "}
                  -{" "}
                  {format(
                    date.to,
                    currentLanguage === "fr" ? "dd MMM y" : "MMM dd, yyyy",
                    {
                      locale: currentLanguage === "fr" ? fr : enUS,
                    }
                  )}
                </>
              ) : (
                format(
                  date.from,
                  currentLanguage === "fr" ? "dd MMM y" : "MMM dd, yyyy",
                  {
                    locale: currentLanguage === "fr" ? fr : enUS,
                  }
                )
              )
            ) : (
              <span>{t("components.dateRangePicker.selectDate")}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex flex-col w-auto p-2 space-y-2">
          <Select onValueChange={handleLastRangeChange}>
            <SelectTrigger>
              <SelectValue
                placeholder={t("components.dateRangePicker.presets.title")}
              />
            </SelectTrigger>
            <SelectContent position="popper">
              <SelectItem value="today">
                {t("components.dateRangePicker.presets.today")}
              </SelectItem>
              <SelectItem value="yesterday">
                {t("components.dateRangePicker.presets.yesterday")}
              </SelectItem>
              <SelectItem value="thisweek">
                {t("components.dateRangePicker.presets.thisWeek")}
              </SelectItem>
              <SelectItem value="previousweek">
                {t("components.dateRangePicker.presets.lastWeek")}
              </SelectItem>
              <SelectItem value="last7days">
                {t("components.dateRangePicker.presets.last7Days")}
              </SelectItem>
              <SelectItem value="thismonth">
                {t("components.dateRangePicker.presets.thisMonth")}
              </SelectItem>
              <SelectItem value="previousmonth">
                {t("components.dateRangePicker.presets.lastMonth")}
              </SelectItem>
            </SelectContent>
          </Select>
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date as DateRange}
            onSelect={(date) => handleDateChange(date)}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
